// src/layout/titlebar.jsx
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate, matchRoutes } from "react-router-dom";
import _ from "lodash";

import { public_restricted_routes, public_routes, private_routes } from "@configs/Router";
import { changeLanguage } from "@actions";
import { storePaths, reducePaths } from "@utils/Tools";

import {
  AppBar, Toolbar, IconButton, Box, Link, Drawer, List, Container, SwipeableDrawer, Button } from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import { FiChevronLeft, FiCheck, FiChevronRight  } from "react-icons/fi";
// import { IoLanguage } from "react-icons/io5";

const INITIAL_STATE = {
  showTutorBtn: false,
  showBackBtn: false,
  backUrl: null,
  tutorUrl: null,
  showLogo: true,
  noHeader: false,
  languageButton: true,
};

const BRAND = {
  darkPurple: "#3B235D",
  navText: "black",
  langBlue: "#3B235D",
  headerHeroBg: "#F5f5f5", // MUST match index.jsx heroWrap bg
};

const TitleBar = memo((props) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { mode } = useSelector((state) => state.general);

  const [option, setOption] = useState(INITIAL_STATE);
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const current_path = location?.pathname;

  const [sheet, setSheet] = useState({ bottom: false });

  const toggleSheet = (anchor, open) => (event) => {
    if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setSheet({ ...sheet, [anchor]: open });
  };

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  useEffect(() => {
    let public_restricted_detail = matchRoutes(public_restricted_routes, current_path)?.[0]?.["route"];
    let public_detail = matchRoutes(public_routes, current_path)?.[0]?.["route"];
    let private_detail = matchRoutes(private_routes, current_path)?.[0]?.["route"];

    if (_.size(public_restricted_detail) > 0) {
      setOption((o) => ({
        ...o,
        showBackBtn: public_restricted_detail?.showBackBtn,
        showTutorBtn: public_restricted_detail?.showTutorBtn,
        languageButton: public_restricted_detail?.showTutorBtn,
        showLogo: public_restricted_detail?.showLogo,
        tutorUrl: public_restricted_detail?.tutorUrl,
        backUrl: public_restricted_detail?.backUrl,
        backTitle: public_restricted_detail?.backTitle || "",
        noHeader: public_restricted_detail?.noHeader,
      }));
    } else if (_.size(public_detail) > 0) {
      setOption((o) => ({
        ...o,
        showBackBtn: public_detail?.showBackBtn,
        showTutorBtn: public_detail?.showTutorBtn,
        languageButton: public_detail?.languageButton,
        showLogo: public_detail?.showLogo,
        tutorUrl: public_detail?.tutorUrl,
        backUrl: public_detail?.backUrl,
        backTitle: public_detail?.backTitle || "",
        noHeader: public_detail?.noHeader,
      }));
    } else if (_.size(private_detail) > 0) {
      setOption((o) => ({
        ...o,
        showBackBtn: private_detail?.showBackBtn,
        showTutorBtn: private_detail?.showTutorBtn,
        languageButton: private_detail?.languageButton,
        showLogo: private_detail?.showLogo,
        tutorUrl: private_detail?.tutorUrl,
        backUrl: private_detail?.backUrl,
        backTitle: private_detail?.backTitle || "",
        noHeader: private_detail?.noHeader,
      }));
    } else {
      setOption(INITIAL_STATE);
    }
  }, [current_path]);

  useEffect(() => {
    document.documentElement.dir = i18n.resolvedLanguage === "ur" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.resolvedLanguage;
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const goBack = () => {
    if (_.size(location?.state?.from) > 0) {
      let path = _.size(location?.state?.from) > 0 ? _.last(location?.state?.from) : "/";
      let pathListing = reducePaths(location);
      let urlLink = path?.pathname;
      if (path?.search !== "") urlLink = urlLink + path?.search;
      navigate(urlLink, { state: { from: pathListing } });
    } else {
      navigate("/");
    }
  };

  const handleLanguageToggle = () => {
    const newLanguage = i18n.resolvedLanguage === "en" ? "cn" : "en";
    i18n.changeLanguage(newLanguage);
    dispatch(changeLanguage(newLanguage));
  };

  const languageOptions = [
    { code: "en", label: "English (US)" },
    { code: "cn", label: "简体中文" },
  ];

  const navItems = [
    { title: t('title.about'), link: "#about", target: "_top" },
    { title: t('title.service'), link: "#service", target: "_top" },
    { title: t('title.philosophy'), link: "#philosophy", target: "_top" },
    { title: t('conclusion.title'), link: "#conclusion", target: "_top" },
  ];

  const drawerWidth = 260;

  const { window } = props;
  const container = window !== undefined ? () => window().document.body : undefined;

  if (option?.noHeader === true) {
    return (
      <>
        {option?.showBackBtn && (
          <div className="flex-sb-m p-lr-26 p-t-20 p-b-10" style={{ zIndex: 3 }}>
            {option?.backUrl ? (
              <Link
                underline="none"
                to={option?.backUrl}
                component={RouterLink}
                state={{ from: storePaths(location) }}
                className="flex-m"
                style={{ color: "#fff" }}
              >
                <FiChevronLeft className="cl-text" />
                <p style={{ margin: 0, paddingLeft: 10, fontSize: 14 }}>{option?.backTitle}</p>
              </Link>
            ) : (
              <div className="flex-m pointer txt-white" onClick={goBack}>
                <FiChevronLeft className="cl-text" />
                <p style={{ margin: 0, paddingLeft: 10, fontSize: 14 }}>{option?.backTitle}</p>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Mobile drawer */}
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          <Box onClick={handleDrawerToggle}>
            <div className="p-lr-21 p-tb-12">
              <img src="/images/brandx_black.png" alt="BrandX" style={{ height: 43 }} />
            </div>
            <div className="divider-row" />
            <List>
              {navItems.map((item) => (
                <div key={item.title}>
                  <Link to={item.link} target={item.target} underline="none" component={RouterLink} state={{ from: storePaths(location) }}>
                    <div style={{ padding: "14px 18px", color: BRAND.navText }}>
                      <p style={{ margin: 0, fontSize: 13 }}>{item.title}</p>
                    </div>
                  </Link>
                </div>
              ))}

              {/* <div className="pointer" style={{ padding: "14px 18px", color: BRAND.navText, }} onClick={toggleSheet("bottom", true)}>
                <p className="fs-13">Language</p>
              </div> */}
              <div className="pointer p-tb-14 p-lr-18 fs-13 w-full flex-sb-m" onClick={handleLanguageToggle} style={{ color: BRAND.navText }}>
                <p>{t('general.language')}</p>
                <div className="flex-m gap-5">
                  {i18n.resolvedLanguage === "en" ?
                    <p style={{ color: BRAND.langBlue }}>中文</p>
                    :
                    <p style={{ color: BRAND.langBlue }}>English</p>
                  }
                  <FiChevronRight />
                </div>
              </div>

              {/* <div style={{ padding: "18px" }}>
                <Link to="https://tsglobal.shop/" underline="none" target="_blank" component={RouterLink}>
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    style={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 800,
                      padding: "12px 18px",
                      background: BRAND.darkPurple,
                      color: "#fff",
                    }}
                  >
                    {t('button.login)}
                  </Button>
                </Link>
              </div> */}
            </List>

            <SwipeableDrawer
              anchor="bottom"
              open={sheet.bottom}
              onClose={toggleSheet("bottom", false)}
              onOpen={toggleSheet("bottom", true)}
            >
              <div style={{ padding: "22px 20px" }}>
                <p style={{ margin: 0, fontWeight: 800 }}>Select language</p>
                <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
                  {languageOptions.map((lang) => (
                    <div
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        dispatch(changeLanguage(lang.code));
                        setSheet({ ...sheet, bottom: false });
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 10px",
                        borderRadius: 12,
                        cursor: "pointer",
                        background: "rgba(0,0,0,0.04)",
                      }}
                    >
                      <span style={{ fontWeight: i18n.resolvedLanguage === lang.code ? 800 : 500 }}>{lang.label}</span>
                      {i18n.resolvedLanguage === lang.code && <FiCheck size={18} />}
                    </div>
                  ))}
                </div>
              </div>
            </SwipeableDrawer>
          </Box>
        </Drawer>
      </nav>

      <AppBar
        position="sticky"
        elevation={0}
        style={{
          backgroundColor: BRAND.headerHeroBg,
          boxShadow: "none",
          borderBottom: "none",
          marginBottom: 0,
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container>
          <Toolbar
            disableGutters
            style={{
              minHeight: 76,
              padding: "0 4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Link component={RouterLink} to="/" target="_top" underline="none">
                <img src="/images/brandx_black.png" alt="BrandX" style={{ height: 47 }} />
              </Link>
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={handleDrawerToggle} sx={{ display: { md: "none" } }} style={{ marginRight: 6 }}>
                <MenuIcon />
              </IconButton>

              <Box sx={{ display: { xs: "none", sm: "none", md: "flex" } }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 34,
                    color: BRAND.navText,
                  }}
                >
                    {navItems.map((nav, index) => (
                    <Link
                      key={index}
                      to={nav.link}
                      target="_top"
                      underline="none"
                      component={RouterLink}
                      style={{ color: BRAND.langBlue, opacity: 1, fontSize: 16, fontWeight: 530 }}
                    >
                      {nav.title}
                    </Link>
                  ))}

                  <IconButton
                    size="small"
                    aria-label="language"
                    onClick={handleLanguageToggle}
                    style={{ marginLeft: 2, marginRight: 6}}
                  >
                    {i18n.resolvedLanguage === "en" ?
                      <p style={{ color: BRAND.langBlue }}><b>中</b></p>
                      :
                      <p style={{ color: BRAND.langBlue }}><b>EN</b></p>
                    }
                    {/* <IoLanguage size={22} color={BRAND.langBlue} /> */}
                  </IconButton>

                  {/* <Link to="/" underline="none" target="_blank" component={RouterLink}>
                    <Button
                      variant="contained"
                      disableElevation
                      style={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 800,
                        fontSize: 16,
                        padding: "6px 10px",
                        color: "#fff",
                        background: BRAND.darkPurple, // same as Contact Us
                      }}
                    >
                      {t('button.login')}
                    </Button>
                  </Link> */}
                </div>
              </Box>
            </div>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
});

export default TitleBar;
